/**
 * Rotation at the boundary.
 *
 * Stored in radian (invariant 5: canonical SI internally), presented in degrees,
 * because degrees are what a designer reads and types. Every conversion between
 * the two goes through here, so the inspector, menus and any future rotation
 * field cannot disagree about which unit a number is in.
 */

const TURN = 2 * Math.PI;
const QUARTER = Math.PI / 2;

export function degreesFromRadians(radians: number): number {
  return (radians * 180) / Math.PI;
}

export function radiansFromDegrees(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/** Into [0, 2π), so a rotation has one stored form however it was reached. */
export function normaliseRotation(radians: number): number {
  const wrapped = radians % TURN;
  return wrapped < 0 ? wrapped + TURN : wrapped;
}

/**
 * Turn by whole quarters, landing exactly on a quarter. Snapping the result,
 * not just the step, means a rotation that was off-quarter (an import, a future
 * free-angle field) is squared up by the first turn, and repeated turns never
 * accumulate floating-point drift.
 */
export function turnByQuarters(radians: number, quarterTurns: number): number {
  const quarters = (((Math.round(radians / QUARTER) + quarterTurns) % 4) + 4) % 4;
  return quarters * QUARTER;
}

/** For display: "90°". Rounded to a tenth, normalised to 0-360. */
export function formatRotation(radians: number): string {
  const degrees = Math.round(degreesFromRadians(normaliseRotation(radians)) * 10) / 10;
  return `${degrees === 360 ? 0 : degrees}\u00b0`;
}

/**
 * Parse what a user typed, in degrees: "90", "90°", "-90". Returns radian,
 * normalised, or null when it is not a finite number.
 */
export function parseRotation(text: string): number | null {
  const cleaned = text.trim().replace(/\u00b0$/, "").trim();
  if (cleaned === "") {
    return null;
  }
  const degrees = Number(cleaned);
  return Number.isFinite(degrees) ? normaliseRotation(radiansFromDegrees(degrees)) : null;
}
